import { useState } from 'react'
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ExclamationCircleIcon,
  ForwardIcon,
  TrashIcon,
} from '@heroicons/react/24/outline'
import type { ChatPlanRead, PlanStepInput, PlanStepRead } from '../../client'
import { apiErrorMessage } from '../../lib/api'
import { useChat } from '../../providers/chat/context'
import SaveWorkflowButton from '../workflows/SaveWorkflowButton'
import Spinner from './Spinner'

const BUTTON = 'h-7 rounded-md px-2.5 text-[11px] font-medium transition disabled:cursor-not-allowed disabled:opacity-60'
const PRIMARY = `${BUTTON} bg-linear-to-b from-stone-700 to-stone-950 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.12),0_1px_2px_rgba(28,25,23,0.2)] hover:opacity-90`
const OUTLINE = `${BUTTON} border border-stone-200 bg-stone-100 text-stone-700 hover:bg-stone-200`
const MUTED = `${BUTTON} text-stone-500 hover:bg-stone-200 hover:text-stone-700`
const INPUT =
  'w-full rounded-lg border border-stone-300 bg-white px-2.5 py-1.5 text-xs text-stone-900 placeholder:text-stone-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-100 focus:outline-none'
const CHIP = 'rounded-md bg-stone-200 px-1.5 py-px text-[10px] font-medium text-stone-600'

const STATUS_LABELS: Record<ChatPlanRead['status'], string> = {
  proposed: 'Waiting for approval',
  running: 'Running',
  paused: 'Paused',
  completed: 'Done',
  cancelled: 'Cancelled',
  revised: 'Replaced',
}

type Mode = 'view' | 'edit' | 'revise'

function credits(value: number) {
  if (value <= 0) return null
  return value < 1 ? '< 1 credit' : `~${Math.round(value)} credit${value >= 1.5 ? 's' : ''}`
}

function StepIcon({ step }: { step: PlanStepRead }) {
  if (step.status === 'running') return <Spinner className="size-3.5 border-purple-200 border-t-purple-700" />
  if (step.status === 'done') return <CheckCircleIcon className="size-4 text-purple-700" />
  if (step.status === 'failed') return <ExclamationCircleIcon className="size-4 text-red-600" />
  if (step.status === 'skipped') return <ForwardIcon className="size-4 text-stone-400" />
  return (
    <span className="flex size-4 items-center justify-center rounded-full border border-stone-300 text-[9px] font-semibold text-stone-500">
      {step.position}
    </span>
  )
}

function StepEditor({
  steps,
  onSave,
  onCancel,
}: {
  steps: PlanStepInput[]
  onSave: (steps: PlanStepInput[]) => Promise<void>
  onCancel: () => void
}) {
  const [draft, setDraft] = useState(steps)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const valid = draft.length > 0 && draft.every((step) => step.title.trim().length > 0)

  function change(index: number, patch: Partial<PlanStepInput>) {
    setDraft((current) => current.map((step, at) => (at === index ? { ...step, ...patch } : step)))
  }

  async function save() {
    setSaving(true)
    setError(null)
    try {
      await onSave(draft.map((step) => ({ ...step, title: step.title.trim(), detail: step.detail?.trim() ?? '' })))
    } catch (err) {
      setError(apiErrorMessage(err))
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2 px-3 pb-3">
      {draft.map((step, index) => (
        <div key={index} className="flex gap-2">
          <span className="mt-2 w-4 shrink-0 text-center text-[11px] font-semibold text-stone-500">{index + 1}</span>
          <div className="min-w-0 flex-1 space-y-1">
            <input
              aria-label={`Step ${index + 1} title`}
              value={step.title}
              onChange={(event) => change(index, { title: event.target.value })}
              className={INPUT}
            />
            <textarea
              aria-label={`Step ${index + 1} detail`}
              value={step.detail ?? ''}
              rows={2}
              onChange={(event) => change(index, { detail: event.target.value })}
              className={`${INPUT} resize-none`}
            />
          </div>
          <button
            type="button"
            aria-label={`Remove step ${index + 1}`}
            disabled={draft.length === 1}
            onClick={() => setDraft((current) => current.filter((_, at) => at !== index))}
            className="mt-1 flex size-7 shrink-0 items-center justify-center rounded-md text-stone-400 transition hover:bg-stone-200 hover:text-red-600 disabled:opacity-30"
          >
            <TrashIcon className="size-3.5" />
          </button>
        </div>
      ))}
      {error && <p className="text-[11px] text-red-600">{error}</p>}
      <div className="flex justify-end gap-1.5">
        <button type="button" onClick={onCancel} className={MUTED}>
          Discard
        </button>
        <button type="button" disabled={!valid || saving} onClick={() => void save()} className={PRIMARY}>
          {saving ? 'Saving…' : 'Save steps'}
        </button>
      </div>
    </div>
  )
}

export default function PlanCard({ plan }: { plan: ChatPlanRead }) {
  const { busy, decidePlan, editPlan } = useChat()
  const [mode, setMode] = useState<Mode>('view')
  const [feedback, setFeedback] = useState('')

  const total = credits(plan.steps.reduce((sum, step) => sum + step.estimated_credits, 0))
  const disabled = busy !== null
  const finished = plan.status === 'revised' || plan.status === 'cancelled'
  const doneCount = plan.steps.filter((step) => step.status === 'done' || step.status === 'skipped').length

  return (
    <div
      className={`w-full max-w-md self-start overflow-hidden rounded-xl border border-stone-200 bg-stone-100 shadow-[0_1px_2px_rgba(28,25,23,0.06)] ${
        finished ? 'opacity-70' : ''
      }`}
    >
      <div className="flex items-center gap-2.5 p-3">
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-stone-200">
          <ClipboardDocumentListIcon className="size-3.5 text-purple-700" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-stone-900">Plan · {plan.steps.length} steps</p>
          <p className="text-[11px] text-stone-500">
            {STATUS_LABELS[plan.status]}
            {plan.status === 'running' && ` · ${doneCount} of ${plan.steps.length} done`}
          </p>
        </div>
        {total && !finished && <span className={CHIP}>{total}</span>}
      </div>

      {plan.status !== 'revised' &&
        (mode === 'edit' ? (
          <StepEditor
            steps={plan.steps.map(({ title, detail, tools }) => ({ title, detail, tools }))}
            onCancel={() => setMode('view')}
            onSave={async (steps) => {
              await editPlan(plan.id, steps)
              setMode('view')
            }}
          />
        ) : (
          <ol className="space-y-2.5 px-3 pb-3">
            {plan.steps.map((step) => (
              <li key={step.position} className="flex gap-2">
                <span className="mt-px flex w-4 shrink-0 justify-center">
                  <StepIcon step={step} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p
                      className={`text-xs font-medium ${
                        step.status === 'skipped' ? 'text-stone-400 line-through' : 'text-stone-900'
                      }`}
                    >
                      {step.title}
                    </p>
                    {credits(step.estimated_credits) && plan.status === 'proposed' && (
                      <span className={`${CHIP} shrink-0`}>{credits(step.estimated_credits)}</span>
                    )}
                  </div>
                  {step.detail && <p className="mt-0.5 text-[11px] leading-snug text-stone-500">{step.detail}</p>}
                  {step.summary && (
                    <p
                      className={`mt-1 text-[11px] leading-snug ${
                        step.status === 'failed' ? 'text-red-600' : 'text-stone-600'
                      }`}
                    >
                      {step.summary}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        ))}

      {mode === 'revise' && plan.status === 'proposed' && (
        <div className="space-y-1.5 px-3 pb-3">
          <textarea
            aria-label="What should change"
            value={feedback}
            rows={2}
            autoFocus
            placeholder="What should change? e.g. use 6 images and target women 25–34"
            onChange={(event) => setFeedback(event.target.value)}
            className={`${INPUT} resize-none`}
          />
        </div>
      )}

      {plan.status === 'proposed' && mode !== 'edit' && (
        <div className="flex flex-wrap justify-end gap-1.5 border-t border-stone-200 px-3 py-2">
          {mode === 'revise' ? (
            <>
              <button type="button" onClick={() => setMode('view')} className={MUTED}>
                Back
              </button>
              <button
                type="button"
                disabled={disabled || feedback.trim().length === 0}
                onClick={() => void decidePlan(plan.id, 'revise', feedback.trim())}
                className={PRIMARY}
              >
                Send changes
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={disabled}
                onClick={() => void decidePlan(plan.id, 'cancel')}
                className={MUTED}
              >
                Cancel
              </button>
              <button type="button" disabled={disabled} onClick={() => setMode('edit')} className={OUTLINE}>
                Edit
              </button>
              <button type="button" disabled={disabled} onClick={() => setMode('revise')} className={OUTLINE}>
                Ask for changes
              </button>
              <button
                type="button"
                disabled={disabled}
                onClick={() => void decidePlan(plan.id, 'approve')}
                className={PRIMARY}
              >
                Approve and run
              </button>
            </>
          )}
        </div>
      )}

      {plan.status === 'completed' && !plan.workflow_id && (
        <div className="flex justify-end border-t border-stone-200 px-3 py-2">
          <SaveWorkflowButton plan={plan} className={OUTLINE} />
        </div>
      )}

      {plan.status === 'paused' && (
        <div className="flex flex-wrap justify-end gap-1.5 border-t border-stone-200 px-3 py-2">
          <button
            type="button"
            disabled={disabled}
            onClick={() => void decidePlan(plan.id, 'cancel')}
            className={MUTED}
          >
            Stop plan
          </button>
          <button type="button" disabled={disabled} onClick={() => void decidePlan(plan.id, 'skip')} className={OUTLINE}>
            Skip step
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => void decidePlan(plan.id, 'retry')}
            className={PRIMARY}
          >
            Retry step
          </button>
        </div>
      )}
    </div>
  )
}
